import { useCallback, useEffect, useRef, useState } from 'react';
import { itemsService } from '../../../../services/itemsService';
import { ItemOverviewDto } from '../../../../types/itemOverview';

// StrictMode dobbeltkjører effekter i dev, og bytte tilbake til samme vare skjer ofte raskt.
// Del ett kall per vare/lager i et kort vindu. reload() går alltid forbi.
const SHARE_WINDOW_MS = 3000;
const sharedRequests = new Map<string, { promise: Promise<ItemOverviewDto | null>; at: number }>();

const fetchShared = (itemNo: string, warehouseId: string | null, force: boolean) => {
  const key = `${itemNo}|${warehouseId ?? ''}`;
  const existing = sharedRequests.get(key);
  if (!force && existing && Date.now() - existing.at < SHARE_WINDOW_MS) {
    return existing.promise;
  }
  const promise = itemsService.getOverview(itemNo, warehouseId);
  sharedRequests.set(key, { promise, at: Date.now() });
  promise.catch(() => sharedRequests.delete(key));
  return promise;
};

/**
 * Laster varekortet (GET items/current/{itemNo}/overview). `notFound` skiller «finnes ikke»
 * fra nettverks-/serverfeil. Svar på et gammelt varenummer forkastes hvis brukeren har byttet vare.
 */
export function useItemOverview(itemNo: string | null, warehouseId: string | null) {
  const [overview, setOverview] = useState<ItemOverviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async (force = false) => {
    if (!itemNo) {
      setOverview(null);
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const result = await fetchShared(itemNo, warehouseId, force);
      if (requestId !== requestRef.current) return;
      setOverview(result);
      setNotFound(result === null);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Error loading item overview:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [itemNo, warehouseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(() => load(true), [load]);

  return { overview, loading, error, notFound, reload };
}
